import React from 'react';
import { Button, Card, Select, TextField, Typography } from '../../design-system';
import {
  SW_HEIGHT_CLAMP_DEFAULTS,
  SwHeightMethod
} from '../../@types/swHeight/types';
import { SwHeightPatchApi } from './useSwHeightSettingsPatch';
import {
  CLAMP_FIELDS,
  GRADIENT_CONVENTION,
  METHOD_HINT,
  METHOD_OPTIONS,
  PARAMETER_ROWS,
  SECTION_BUTTON_LABELS,
  SwHeightSection,
  numberOrNull
} from './swHeightSettings';
import styles from './SwHeightFunctionPanel.module.css';

const SECTIONS: SwHeightSection[] = ['cutoffs', 'parameters', 'rock-types'];

export interface SwHeightFunctionPanelProps {
  api: SwHeightPatchApi;
  /** Which section is open, so its button can report it. */
  openSection?: SwHeightSection | null;
  onOpenSection?: (section: SwHeightSection) => void;
  /** True while a debounced recompute is pending. */
  computing?: boolean;
  disabled?: boolean;
}

/**
 * The Sw-height function's own inputs, sized for a narrow rail beside the
 * charts.
 *
 * Method leads because it decides what the other two parameter groups even
 * contain — it swaps the tendency rows between Lambda and a/b/Pc max, and
 * swaps the rock-type grid's columns to match. FWL, the gradient difference
 * and the parameter variable are its arguments.
 *
 * The three bulky groups sit behind buttons. Each button carries a count of
 * how many values in that group depart from their default, because a section
 * you cannot see is a section whose edits you cannot notice.
 */
export const SwHeightFunctionPanel: React.FC<SwHeightFunctionPanelProps> = ({
  api,
  openSection = null,
  onOpenSection,
  computing = false,
  disabled = false
}) => {
  const { settings, method, clamps, overrides, patch } = api;

  const changedCounts: Record<SwHeightSection, number> = {
    cutoffs: CLAMP_FIELDS.filter(
      field => clamps[field.key] !== SW_HEIGHT_CLAMP_DEFAULTS[field.key]
    ).length,
    parameters: PARAMETER_ROWS[method].filter(
      row => settings[row.key]?.source === 'manual'
    ).length,
    'rock-types': Object.keys(overrides).length
  };

  const badgeLabel = (section: SwHeightSection): string | null => {
    const count = changedCounts[section];
    if (count === 0) return null;
    return section === 'cutoffs' ? `${count} changed` : `${count} manual`;
  };

  // fullWidth is required: Card is `width: fit-content` by default, so without
  // it the panel shrinks to its widest control and leaves the rest of the rail
  // track empty to its right.
  return (
    <Card
      padding="md"
      fullWidth
      className={styles.panel}
      data-testid="sw-height-function-card"
    >
      <div className={styles.panelHeader}>
        <Typography.Heading level={4}>Sw-height function</Typography.Heading>
        {computing && (
          <span className={styles.computing} data-testid="sw-height-computing">
            Recalculating…
          </span>
        )}
      </div>

      {/* Select drops unknown props, so this is addressed by label. */}
      <div className={styles.modeControls} data-testid="mode-controls">
        <Select
          label="Method"
          hint={METHOD_HINT[method]}
          size="sm"
          fullWidth
          options={METHOD_OPTIONS}
          value={method}
          disabled={disabled}
          onChange={value => patch({ method: value as SwHeightMethod })}
        />
      </div>

      <div className={styles.inputs} data-testid="calibration-inputs">
        <TextField
          label="Free water level (TVDSS)"
          size="sm"
          type="number"
          fullWidth
          value={settings.fwl ?? ''}
          disabled={disabled}
          data-testid="fwl-input"
          onChange={event => patch({ fwl: numberOrNull(event.target.value) })}
        />
        <TextField
          label="Gradient difference (psi/ft)"
          hint={GRADIENT_CONVENTION}
          size="sm"
          type="number"
          fullWidth
          value={settings.grad_diff ?? ''}
          disabled={disabled}
          data-testid="grad-diff-input"
          onChange={event =>
            patch({ grad_diff: numberOrNull(event.target.value) })
          }
        />
        <Select
          label="Parameter variable"
          size="sm"
          fullWidth
          options={[
            { value: 'rqi', label: 'RQI' },
            { value: 'fzi', label: 'FZI' }
          ]}
          value={settings.x_axis}
          disabled={disabled}
          onChange={value => patch({ x_axis: value as 'rqi' | 'fzi' })}
        />
      </div>

      <div className={styles.sectionButtons} data-testid="section-buttons">
        {SECTIONS.map(section => {
          const badge = badgeLabel(section);
          return (
            <Button
              key={section}
              variant="secondary"
              size="sm"
              fullWidth
              disabled={disabled || !onOpenSection}
              aria-expanded={openSection === section}
              data-testid={`open-${section}`}
              onClick={() => onOpenSection?.(section)}
            >
              <span className={styles.sectionButtonLabel}>
                {SECTION_BUTTON_LABELS[section]}
                {badge && (
                  <span
                    className={styles.badge}
                    data-testid={`badge-${section}`}
                  >
                    {badge}
                  </span>
                )}
              </span>
            </Button>
          );
        })}
      </div>
    </Card>
  );
};

export default SwHeightFunctionPanel;
